import Image from "next/image";
import Link from "next/link";
import { Eyebrow, displayFont } from "@/components/content-blocks/band-helpers";
import type { ContentBlock } from "@/lib/storefront-types";

type LondonBoroughsSectionProps = {
  block: Extract<ContentBlock, { kind: "londonBoroughs" }>;
  stackPosition?: "single" | "start" | "middle" | "end";
};

function resolveSectionPadding(
  stackPosition: LondonBoroughsSectionProps["stackPosition"],
) {
  switch (stackPosition) {
    case "start":
      return "pt-7 pb-8 sm:pt-9 sm:pb-10 lg:pt-10 lg:pb-12";
    case "middle":
      return "py-8 sm:py-10 lg:py-12";
    case "end":
      return "pt-7 pb-6 sm:pt-9 sm:pb-8 lg:pt-10 lg:pb-10";
    default:
      return "py-12 sm:py-16 lg:py-20";
  }
}

export function LondonBoroughsSection({
  block,
  stackPosition = "single",
}: LondonBoroughsSectionProps) {
  if (block.links.length === 0) {
    return null;
  }

  const sectionPaddingClass = resolveSectionPadding(stackPosition);
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const isDark = !isLight && !isFrozen;
  // The Dark variant shares the Light theme's background + copy treatment, but
  // keeps its own dark borough buttons (filled solid so they stay legible on
  // the light surface). Frozen is unchanged.
  const lightShell = isLight || isDark;
  const sectionClass = lightShell
    ? "bg-[#f5f9f2] storefront-text-heading"
    : "storefront-surface-frozen text-white";
  const overlayClass = isFrozen ? "storefront-accent-radial" : "";
  const linkClass = isLight
    ? "border storefront-border-soft bg-white hover:-translate-y-px hover:border-[#bfd2ba] hover:bg-[#f8fbf6] focus-visible:border-[#bfd2ba] focus-visible:bg-[#f8fbf6]"
    : isFrozen
      ? "storefront-border-primary-soft storefront-surface-frozen-soft border hover:-translate-y-px hover:border-[var(--storefront-primary)] hover:bg-white/12 focus-visible:border-[var(--storefront-primary)] focus-visible:bg-white/12"
      : "border border-white/12 storefront-surface-dark hover:-translate-y-px hover:border-white/24 hover:brightness-125 focus-visible:border-white/30 focus-visible:brightness-125";
  const badgeShellClass = isLight
    ? "border storefront-border-soft bg-[#f7f4ed] shadow-[0_18px_32px_-22px_rgba(0,39,0,0.24)]"
    : "border border-white/14 bg-[#f7f4ed] shadow-[0_18px_32px_-22px_rgba(0,0,0,0.55)]";
  const labelClass = isLight
    ? "storefront-text-heading group-hover:translate-x-0.5"
    : "text-white group-hover:translate-x-0.5";

  return (
    <section
      className={`relative overflow-hidden ${sectionClass} ${sectionPaddingClass}`}
    >
      <div className={`pointer-events-none absolute inset-0 ${overlayClass}`} />

      <div className="relative mx-auto max-w-[90rem] px-5 sm:px-8 lg:px-12">
        <div className="max-w-[56rem]">
          <Eyebrow inverse={!lightShell} className={lightShell ? "storefront-text-muted" : ""}>
            London delivery areas
          </Eyebrow>
          <h2
            className={`${displayFont.className} mt-4 text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight ${
              lightShell ? "storefront-text-heading" : "text-white"
            }`}
          >
            {block.sectionTitle ?? "Delivery Across London"}
          </h2>
          {block.introHtml ? (
            <div
              className={`mt-5 max-w-[44rem] text-base leading-7 sm:text-lg ${
                lightShell ? "storefront-text-body" : "text-white/74"
              }`}
              dangerouslySetInnerHTML={{ __html: block.introHtml }}
            />
          ) : null}
        </div>

        <div className="mt-8 grid gap-3.5 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:gap-5">
          {block.links.map((link) => (
            <Link
              key={link.path}
              href={link.path}
              className={`group flex min-h-[6.4rem] items-center gap-4 rounded-[1.6rem] px-5 py-4 text-left transition-all duration-200 sm:min-h-[6.85rem] sm:px-6 sm:py-5 ${linkClass}`}
            >
              <span
                className={`relative inline-flex h-[4.15rem] w-[4.15rem] shrink-0 items-center justify-center overflow-hidden rounded-[1.45rem] sm:h-[4.55rem] sm:w-[4.55rem] ${badgeShellClass}`}
              >
                {link.image ? (
                  <>
                    <Image
                      src={link.image.url}
                      alt={link.image.alt || `${link.label} logo`}
                      fill
                      sizes="(min-width: 640px) 73px, 66px"
                      className={`object-contain p-2 saturate-0 brightness-[0.28] contrast-[1.15] transition-opacity duration-200 ${
                        link.activeImage
                          ? "opacity-100 group-hover:opacity-0 group-focus-visible:opacity-0 group-active:opacity-0"
                          : "opacity-100"
                      }`}
                    />
                    {link.activeImage ? (
                      <Image
                        src={link.activeImage.url}
                        alt=""
                        aria-hidden="true"
                        fill
                        sizes="(min-width: 640px) 73px, 66px"
                        className="object-contain p-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 group-active:opacity-100"
                      />
                    ) : null}
                  </>
                ) : null}
              </span>
              <span
                className={`flex-1 text-[1rem] font-semibold leading-5 transition-transform duration-200 sm:text-[1.07rem] sm:leading-6 ${labelClass}`}
              >
                {link.label}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
